'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ComponentProps } from 'react';
import { QueryApi } from '@/features/query/api';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import './user-button.scss';

type Props = ComponentProps<typeof Layout>;
export const UserButton = ({ media, ...props }: Props) => {
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const customerOrdersQuery = useQuery(
        QueryApi.Customer.queries.Orders({ limit: PAGINATION_LIMIT, offset: 0 }),
    );
    const t = useTranslations();

    const initials = customerQuery.data
        ? `${customerQuery.data.name.charAt(0)}${customerQuery.data.surname.charAt(0)}`.toUpperCase()
        : '';

    return (
        <Layout
            element="button"
            type="button"
            media={{
                ...media,
                initial: {
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexDirection: undefined,
                    ...media?.initial,
                },
            }}
            {...props}
            onClick={() =>
                customerQuery.data
                    ? EventService.trigger('account:view-detail')
                    : EventService.trigger('account:auth:view-detail')
            }
        >
            {customerQuery.data ? (
                <>
                    <div className="user-button__avatar">
                        {customerQuery.data.avatarUrl ? (
                            <Image
                                src={customerQuery.data.avatarUrl}
                                alt={`${customerQuery.data.name} ${customerQuery.data.surname}`}
                                width={48}
                                height={48}
                                className="user-button__avatar-image"
                                data-testid="user-button-avatar-image"
                            />
                        ) : (
                            <span className="user-button__avatar-initials">{initials}</span>
                        )}
                    </div>

                    <div className="user-button__info">
                        <p className="user-button__name">{`${customerQuery.data.name} ${customerQuery.data.surname}`}</p>
                        <div className="user-button__orders-wrapper">
                            <p className={cn('user-button__orders')}>
                                {t('NAVBAR.ORDERS', { count: customerOrdersQuery.data?.total ?? 0 })}
                            </p>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <Icon name="user" className="user-button__icon" />
                    <div className="user-button__info">
                        <p className="user-button__register">{t('NAVBAR.REGISTER')}</p>
                        <p className="user-button__login">{t('NAVBAR.LOGIN')}</p>
                    </div>
                </>
            )}
        </Layout>
    );
};
